/**
 * What a card down payment call answered. los-api refuses with `{ error, errors? }` (400/404/409/503, and 500 with a
 * generic error); ASP.NET's own 400 carries `errors` as arrays. A call with no response, or a gateway error page with
 * no body of ours, is a lost answer: the request may have reached the server.
 */
export type CardAnswer<T> =
  | { kind: 'ok'; data: T }
  | { kind: 'refused'; status: number; messages: string[] }
  | { kind: 'lost' };

const texts = (value: unknown): string[] => {
  if (typeof value === 'string') return value.trim() ? [value.trim()] : [];
  if (Array.isArray(value)) return value.flatMap(texts);
  return [];
};

export function interpretCardAnswer<T>(status: number, ok: boolean, body: unknown): CardAnswer<T> {
  if (ok) return { kind: 'ok', data: body as T };
  const record = body && typeof body === 'object' ? (body as Record<string, unknown>) : undefined;
  const fieldErrors = record?.errors && typeof record.errors === 'object' ? Object.values(record.errors as object).flatMap(texts) : [];
  const messages = [...texts(record?.error), ...fieldErrors];
  if (messages.length > 0) return { kind: 'refused', status, messages };
  // No answer of ours: a gateway's timeout or error page.
  if (status >= 500 || status === 408) return { kind: 'lost' };
  return { kind: 'refused', status, messages: [`The server answered HTTP ${status}.`] };
}

/** Reads a fetch `Response` (undefined when the request failed to get one) into a `CardAnswer`. */
export async function readCardAnswer<T>(response: Response | null | undefined): Promise<CardAnswer<T>> {
  if (!response) return { kind: 'lost' };
  let body: unknown;
  try {
    const text = await response.text();
    body = text ? JSON.parse(text) : undefined;
  } catch {
    body = undefined;
  }
  return interpretCardAnswer<T>(response.status, response.ok, body);
}

/** The refusal's text for the user: its messages, one per line. */
export const refusalText = (answer: Extract<CardAnswer<unknown>, { kind: 'refused' }>) => answer.messages.join('\n');
